/**
 * PresenceManager
 *
 * Manages host presence reporting to the realtime API.
 *
 * Responsibilities:
 * - Assemble full presence state (dynamic + static)
 * - Send PRESENCE_UPDATE messages to API
 * - Debounce updates to avoid flooding API
 *
 * Send strategy:
 * - status transitions (e.g. hosting → idle, idle → busy) are **state-machine
 *   transitions** that downstream services (matchmaker, fleet view, host
 *   health tracker) treat as discrete events. They are flushed IMMEDIATELY,
 *   so a transient idle inside a fast restart (hosting → idle → busy → hosting
 *   under 1 s) is never collapsed away by the debounce.
 * - non-status changes (playerCount tick, runningGame metadata, hostPeerId)
 *   are coalesced with a 1 s trailing debounce to keep load on the realtime
 *   API low.
 */

import { websocket, PresenceMessageType, ClientType } from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { SessionPresenceState, SessionStores } from '../stores';

type PresenceStatus = SessionPresenceState['status'];

export class PresenceManager {
  private sendDebounceTimer: NodeJS.Timeout | undefined;
  private unsubscribeOnConnected: (() => void) | undefined;
  private presenceListenerId: string | undefined;
  /** Status of the last PRESENCE_UPDATE we actually sent. Used to detect
   *  state-machine transitions and bypass the debounce for them. */
  private lastSentStatus: PresenceStatus | undefined;
  private lastSentAdminIdentity: string | undefined;
  private readonly DEBOUNCE_MS = 1000; // 1 second debounce

  constructor(
    private stores: SessionStores,
    private realtimeService: IRealtimeService,
  ) {}

  /**
   * Initialize the manager.
   */
  init(): void {
    console.log('[PresenceManager] Initialized');

    this.sendPresenceUpdate();

    this.presenceListenerId = this.stores.presence.addListener(() => {
      this.scheduleSend();
    });

    // Re-send presence on WebSocket reconnection.
    // The server deletes HOST_PRESENCE from Redis when the socket closes,
    // so we must re-establish it after every reconnect.
    this.unsubscribeOnConnected = this.realtimeService.onConnected(() => {
      console.log('[PresenceManager] 🌐 WebSocket reconnected - re-sending PRESENCE_UPDATE');
      this.sendPresenceUpdate();
    });
  }

  /**
   * Schedule a presence update.
   *
   * Status transitions are flushed immediately so the realtime API observes
   * every state-machine edge (e.g. hosting → idle → busy → hosting) even when
   * they happen inside the debounce window. Non-status field changes use the
   * 1 s trailing debounce to avoid flooding the API.
   */
  private scheduleSend(): void {
    const currentStatus = this.stores.presence.currentState.status;

    if (currentStatus !== this.lastSentStatus || this.adminIdentity() !== this.lastSentAdminIdentity) {
      this.flushPresenceToApi();
      return;
    }

    if (this.sendDebounceTimer) {
      clearTimeout(this.sendDebounceTimer);
    }

    this.sendDebounceTimer = setTimeout(() => {
      this.sendPresenceUpdate();
    }, this.DEBOUNCE_MS);
  }

  /**
   * Send PRESENCE_UPDATE to API
   */
  private sendPresenceUpdate(): void {
    const presence = this.stores.presence.currentState;
    const staticProps = this.stores.staticProperties.currentState;

    // Assemble full presence (dynamic + static)
    const fullPresence = {
      status: presence.status,
      playerCount: presence.playerCount,
      runningGame: presence.runningGame,
      hostPeerId: presence.hostPeerId,
      remoteHostPeerId: presence.remoteHostPeerId,
      lastUpdated: Date.now(),
      adminSessionActive: presence.adminSessionActive,
      ...(presence.adminSessionActive && presence.adminSessionId
        ? { adminSessionId: presence.adminSessionId }
        : {}),
      ...(presence.adminSessionActive && presence.adminPeerId
        ? { adminPeerId: presence.adminPeerId }
        : {}),

      // Static properties (calculated once on init)
      guestAccount: staticProps.guestAccount,
      geohash: staticProps.geohash,
      coords: staticProps.coords,
      isCloudHost: staticProps.isCloudHost,
    };

    const message = websocket.generators.presence(
      PresenceMessageType.PRESENCE_UPDATE,
      {
        clientType: ClientType.HOST,
        presence: fullPresence,
      }
    );

    console.log('[PresenceManager] 📤 Sending PRESENCE_UPDATE:', {
      status: fullPresence.status,
      playerCount: fullPresence.playerCount,
      runningGame: fullPresence.runningGame?.id,
      isCloudHost: fullPresence.isCloudHost,
    });

    this.realtimeService.sendMessage(message);
    this.lastSentStatus = fullPresence.status;
    this.lastSentAdminIdentity = this.adminIdentity();
  }

  private adminIdentity(): string {
    const state = this.stores.presence.currentState;
    return JSON.stringify([state.adminSessionActive, state.adminSessionId, state.adminPeerId]);
  }

  /**
   * Send the current presence immediately, clearing any pending debounced
   * send. Called from two places:
   *   1. After auth completes so Redis host-presence matches the server
   *      connection state.
   *   2. From scheduleSend() when the status field has changed, to bypass
   *      the trailing debounce so state-machine edges aren't collapsed.
   */
  flushPresenceToApi(): void {
    if (this.sendDebounceTimer) {
      clearTimeout(this.sendDebounceTimer);
      this.sendDebounceTimer = undefined;
    }
    this.sendPresenceUpdate();
  }

  /**
   * Cleanup: cancel any pending debounced send, detach the presence-store
   * listener, and unsubscribe from realtime onConnected. After destroy(),
   * mutations to the presence store must not produce further sends.
   */
  destroy(): void {
    if (this.sendDebounceTimer) {
      clearTimeout(this.sendDebounceTimer);
      this.sendDebounceTimer = undefined;
    }
    if (this.presenceListenerId) {
      this.stores.presence.removeListener(this.presenceListenerId);
      this.presenceListenerId = undefined;
    }
    if (this.unsubscribeOnConnected) {
      this.unsubscribeOnConnected();
      this.unsubscribeOnConnected = undefined;
    }
    console.log('[PresenceManager] Destroyed');
  }
}
